import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  useDisclosure,
} from "@chakra-ui/react";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentTextIcon,
  BookOpenIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { BASE_API_URL } from "./config";
import { a, PALETTES, useAppColorMode } from "./appColors";
import { formatExamBoard } from "./examBoards";
import { buildMatcher, formatSeries, haystack } from "./searchMatch";
import usePaperActions from "./hooks/usePaperActions";

/**
 * The Stande search bar and its command palette (Figma 83:1843 collapsed,
 * 83:1854 focused, 83:1873 for the results layout).
 *
 * There is no search endpoint on the API, so the corpus is pulled once on first
 * open and filtered in the browser: every past paper from /past-papers/detail/,
 * the signed in user's own attempts, and the subject list derived from the
 * papers (no extra request needed for it).
 */

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

const TABS = [
  { id: "all", label: "All" },
  { id: "papers", label: "Past Papers" },
  { id: "subjects", label: "Subjects" },
  { id: "mine", label: "My Papers" },
];

const paperTitle = (p) =>
  `${p.subject_name} ${p.subject_code} · Paper ${p.paper_number ?? ""}${p.paper_variant ?? ""}`.trim();

function Chip({ children }) {
  return (
    <Flex
      align="center"
      justify="center"
      border="0.5px solid"
      borderColor={a.inputBorder}
      borderRadius="4px"
      px="7px"
      py="4px"
      minW="24px"
      color={a.textSub}
      fontSize="12px"
      lineHeight="1"
    >
      {children}
    </Flex>
  );
}

/* ---------------- the collapsed bar in the header ---------------- */

export function SearchTrigger({ onOpen }) {
  return (
    <Flex
      as="button"
      type="button"
      onClick={onOpen}
      align="center"
      justify="space-between"
      width="100%"
      maxW="641px"
      px="14px"
      py="10px"
      borderRadius="24px"
      bg={a.inputBg}
      border="1px solid"
      borderColor={a.inputBorder}
      transition="border-color 0.15s, box-shadow 0.15s"
      _hover={{ borderColor: a.textMuted }}
      aria-label="Search papers"
    >
      <Flex align="center" gap="8px" minWidth={0}>
        <Box as={MagnifyingGlassIcon} boxSize="20px" color={a.text2} flexShrink={0} />
        <Text fontSize="14px" color={a.textSub} noOfLines={1}>
          Search
        </Text>
      </Flex>
      <Flex align="center" gap="4px" flexShrink={0} display={{ base: "none", md: "flex" }}>
        <Chip>{isMac ? "⌘" : "Ctrl"}</Chip>
        <Chip>K</Chip>
      </Flex>
    </Flex>
  );
}

/* ---------------- a single result row ---------------- */

function ResultRow({ icon, title, meta, status, statusTone, active, onSelect, p }) {
  return (
    <Flex
      align="center"
      gap="12px"
      px="14px"
      py="10px"
      borderRadius="12px"
      cursor="pointer"
      bg={active ? p.hover : "transparent"}
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect();
      }}
    >
      <Flex
        align="center"
        justify="center"
        boxSize="32px"
        borderRadius="9px"
        bg={p.surfaceSubtle}
        flexShrink={0}
      >
        <Box as={icon} boxSize="16px" color={p.textSub} />
      </Flex>
      <Box flex="1" minWidth={0}>
        <Text fontSize="14px" color={p.text} noOfLines={1}>
          {title}
        </Text>
        {meta && (
          <Text fontSize="12px" color={p.textMuted} noOfLines={1}>
            {meta}
          </Text>
        )}
      </Box>
      {status && (
        <Text
          flexShrink={0}
          fontSize="11px"
          px="8px"
          py="3px"
          borderRadius="full"
          bg={statusTone === "done" ? "#EAF8FE" : p.surfaceSubtle}
          color={statusTone === "done" ? "#0B6E8C" : p.textSub}
        >
          {status}
        </Text>
      )}
    </Flex>
  );
}

/* ---------------- the palette ---------------- */

export default function SearchCommand() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isDark } = useAppColorMode();
  const p = isDark ? PALETTES.dark : PALETTES.light; // the modal is portalled
  const { solvePaper, openAttempt } = usePaperActions();

  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all");
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(false);
  const [papers, setPapers] = useState([]);
  const [mine, setMine] = useState([]);
  const loadedRef = useRef(false);
  const inputRef = useRef(null);

  /* ⌘K / Ctrl+K anywhere in the app */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        isOpen ? onClose() : onOpen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onOpen, onClose]);

  /* Pull the corpus once, the first time the palette is opened. */
  useEffect(() => {
    if (!isOpen || loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    const token = localStorage.getItem("accessToken");
    const auth = token ? { Authorization: `Bearer ${token}` } : {};

    Promise.allSettled([
      axios.get(`${BASE_API_URL}/api/v1/past-papers/detail/?limit=1000`),
      axios.get(`${BASE_API_URL}/api/v1/user-paper-attempts/detail/?limit=100`, {
        headers: auth,
      }),
    ])
      .then(([papersRes, mineRes]) => {
        if (papersRes.status === "fulfilled" && Array.isArray(papersRes.value.data)) {
          setPapers(papersRes.value.data);
        }
        if (mineRes.status === "fulfilled" && Array.isArray(mineRes.value.data)) {
          setMine(mineRes.value.data);
        }
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  /* Subjects come free from the paper corpus. */
  const subjects = useMemo(() => {
    const seen = new Map();
    papers.forEach((pp) => {
      const key = `${pp.subject_code}-${pp.exam_qualification_code}`;
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          name: pp.subject_name,
          code: pp.subject_code,
          board: pp.exam_board_code,
          qualification: pp.exam_qualification_code,
          count: 1,
        });
      } else {
        seen.get(key).count += 1;
      }
    });
    return [...seen.values()];
  }, [papers]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = buildMatcher(q);

    const mineRows = mine
      .filter((m) =>
        !q
          ? true
          : matches(haystack([m.subject_name, m.subject_code, m.paper_name, m.exam_board_code]))
      )
      .map((m) => ({
        kind: "mine",
        id: m.guid,
        icon: ClockIcon,
        title: `${m.subject_name} ${m.subject_code} · ${m.paper_name || "Paper"}`,
        meta: [formatExamBoard(m.exam_board_code), m.exam_qualification_code]
          .filter(Boolean)
          .join(" · "),
        status: m.completion_status === "COMPLETE" ? "Completed" : "In progress",
        statusTone: m.completion_status === "COMPLETE" ? "done" : "open",
        run: () => openAttempt(m.guid),
      }));

    const paperRows = !q
      ? []
      : papers
          .filter((pp) =>
            matches(
              haystack([
                pp.subject_name,
                pp.subject_code,
                pp.paper_name,
                pp.paper_code,
                formatExamBoard(pp.exam_board_code),
                pp.exam_qualification_code,
                formatSeries(pp.exam_series_month, pp.exam_series_year),
              ])
            )
          )
          .slice(0, 40)
          .map((pp) => ({
            kind: "papers",
            id: pp.guid,
            icon: DocumentTextIcon,
            title: paperTitle(pp),
            meta: [
              formatExamBoard(pp.exam_board_code),
              pp.exam_qualification_code,
              formatSeries(pp.exam_series_month, pp.exam_series_year),
              pp.total_marks ? `${pp.total_marks} marks` : null,
            ]
              .filter(Boolean)
              .join(" · "),
            run: () => solvePaper(pp.guid),
          }));

    const subjectRows = !q
      ? []
      : subjects
          .filter((s) => matches(haystack([s.name, s.code, s.qualification])))
          .slice(0, 8)
          .map((s) => ({
            kind: "subjects",
            id: s.key,
            icon: BookOpenIcon,
            title: `${s.name} ${s.code}`,
            meta: [formatExamBoard(s.board), s.qualification, `${s.count} papers`]
              .filter(Boolean)
              .join(" · "),
            run: () => {
              // Land on the subject's paper list.
              window.location.href = `/subjects?subjectName=${encodeURIComponent(s.name)}`;
            },
          }));

    const all = [...subjectRows, ...paperRows, ...mineRows];
    return tab === "all" ? all : all.filter((r) => r.kind === tab);
  }, [query, tab, papers, mine, subjects, solvePaper, openAttempt]);

  useEffect(() => setCursor(0), [query, tab]);

  const close = useCallback(() => {
    onClose();
    setQuery("");
    setTab("all");
  }, [onClose]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = results[cursor];
      if (row) {
        close();
        row.run();
      }
    } else if (e.key === "Escape") {
      close();
    }
  };

  return (
    <>
      <SearchTrigger onOpen={onOpen} />

      <Modal isOpen={isOpen} onClose={close} isCentered={false} size="xl" initialFocusRef={inputRef}>
        <ModalOverlay bg={p.overlay} backdropFilter="blur(2px)" />
        <ModalContent
          mt="12vh"
          mx="16px"
          bg={p.surface}
          borderRadius="20px"
          overflow="hidden"
          boxShadow="0 24px 60px -20px rgba(0,0,0,0.35)"
          border="1px solid"
          borderColor={p.border}
        >
          {/* the bar itself, focused state */}
          <Flex
            align="center"
            gap="10px"
            px="14px"
            py="10px"
            borderBottom="1px solid"
            borderColor={p.border}
          >
            <Box as={MagnifyingGlassIcon} boxSize="20px" color={p.text2} flexShrink={0} />
            <Input
              ref={inputRef}
              variant="unstyled"
              placeholder="Search papers, subjects, your work"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              fontSize="15px"
              color={p.text}
              _placeholder={{ color: p.textMuted }}
            />
            {query && (
              <Flex
                as="button"
                type="button"
                onClick={() => setQuery("")}
                align="center"
                justify="center"
                p="5px"
                borderRadius="full"
                _hover={{ bg: p.hover }}
                aria-label="Clear search"
              >
                <Box as={XMarkIcon} boxSize="14px" color={p.textSub} />
              </Flex>
            )}
          </Flex>

          {/* type filters */}
          <Flex gap="8px" px="14px" py="10px" borderBottom="1px solid" borderColor={p.border}>
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <Flex
                  key={t.id}
                  as="button"
                  type="button"
                  onClick={() => setTab(t.id)}
                  align="center"
                  px="12px"
                  py="5px"
                  borderRadius="full"
                  border="1px solid"
                  borderColor={active ? p.accent : p.border}
                  bg={active ? p.accentSoft : "transparent"}
                  color={active ? p.accentSoftText || p.accent : p.textSub}
                  fontSize="12px"
                >
                  {t.label}
                </Flex>
              );
            })}
          </Flex>

          {/* results */}
          <Box maxH="380px" overflowY="auto" px="8px" py="8px">
            {loading && (
              <Flex justify="center" py="28px">
                <Spinner size="sm" color={p.accent} />
              </Flex>
            )}

            {!loading && !query && (
              <Text px="14px" py="8px" fontSize="11px" textTransform="uppercase" letterSpacing="0.05em" color={p.textMuted}>
                Recent
              </Text>
            )}

            {!loading &&
              results.map((r, i) => (
                <ResultRow
                  key={`${r.kind}-${r.id}`}
                  icon={r.icon}
                  title={r.title}
                  meta={r.meta}
                  status={r.status}
                  statusTone={r.statusTone}
                  active={i === cursor}
                  onSelect={() => {
                    close();
                    r.run();
                  }}
                  p={p}
                />
              ))}

            {!loading && results.length === 0 && (
              <Flex direction="column" align="center" gap="4px" py="30px" px="20px" textAlign="center">
                <Text fontSize="14px" color={p.text}>
                  {query ? `No results for "${query}"` : "Nothing here yet"}
                </Text>
                <Text fontSize="12px" color={p.textMuted}>
                  {query
                    ? "Try a subject, a paper number, or an exam series."
                    : "Papers you start will show up here."}
                </Text>
              </Flex>
            )}
          </Box>

          {/* footer hints */}
          <Flex
            align="center"
            gap="14px"
            px="16px"
            py="9px"
            borderTop="1px solid"
            borderColor={p.border}
            bg={p.surfaceSubtle}
            fontSize="11px"
            color={p.textMuted}
          >
            <Text>↑↓ to navigate</Text>
            <Text>↵ to open</Text>
            <Text>esc to close</Text>
          </Flex>
        </ModalContent>
      </Modal>
    </>
  );
}
